<script setup>
import { defineProps, onMounted, ref, defineEmits, defineExpose } from "vue";
import FirstName from "@/components/form-fields/first-name/Index.vue";
import LastName from "@/components/form-fields/last-name/Index.vue";
import Email from "@/components/form-fields/email/Index.vue";
import Password from "@/components/form-fields/password/Index.vue";
import RetypePassword from "@/components/form-fields/retype-password/Index.vue";
import {
  customerCreate,
  updateCustomerCartBuyerIdentity,
} from "@/hooks/shopify-hooks/api/useLoginApi";
import { usePostUserinfo } from "@/hooks/api/useISCRMEdm";
import { useBrand, useUcFirst } from "@/hooks/common/useUtils";
import { _CSR_ACCESS_TOKEN, _CSR_LOGIN_EMAIL } from "@/const/shopifyConst.js";
import { useFailMsgBox } from "@/hooks/common/msg-box/useFailMsgBox";
import { _CSR_SHOP_CART_ID } from "@/const/shopifyConst";
import { useCsrFormValidator } from "@/hooks/common/useCsrFormValidator";
import {
  useCustomerName,
  useGetPath,
} from "@/hooks/shopify-hooks/useShopifyMessageEvent";
import { useDispatchGAEvent } from "@/hooks/common/useDispatchGAEvent";
import { useSetStorage, useGetStorage } from "@/hooks/common/useStorage";

const $emitEvents = defineEmits(["onChange"]);

const firstNameInput = ref(null);
const lastNameInput = ref(null);
const emailInput = ref(null);
const passwordInput = ref(null);
const retypePasswordInput = ref(null);
const acceptsMarketing = ref(false);
const showError = ref(false);
const passwordValue = ref("");
const isLoading = ref(false);
const resValue = ref(null);

// const cartId = ref(localStorage.getItem(_CSR_SHOP_CART_ID));
const cartId = ref(useGetStorage(_CSR_SHOP_CART_ID));

const props = defineProps({
  stateKey: {
    type: String,
    default: "",
  },
  loginConfig: {
    type: Object,
    default: null,
  },
});

onMounted(() => {});

const passwordChange = (e) => {
  passwordValue.value = e;
  console.log("passwordChange", e);
};

const submitForm = () => {
  if (!acceptsMarketing.value) {
    showError.value = true;
    return;
  }
  useCsrFormValidator([
    emailInput,
    passwordInput,
    retypePasswordInput,
    firstNameInput,
    lastNameInput,
  ]).then((res) => {
    resValue.value = res;
    const { password, retypePassword } = res;
    if (retypePassword !== password) {
      useFailMsgBox(props.loginConfig.rePWSecurityPrompt, () => {});
      return;
    }
    // 进行极验校验
    window.captchaObj &&
      window.captchaObj.showCaptcha &&
      window.captchaObj.showCaptcha();
  });
};

const registerCustomer = async (validateParams) => {
  const { email, password, firstName, lastName } = resValue.value;
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    const res = await customerCreate(
      {
        email,
        password,
        firstName,
        lastName,
        acceptsMarketing: true,
      },
      validateParams,
      props.loginConfig
    );
    if (res?.customerAccessToken?.accessToken) {
      useDispatchGAEvent("__GA_SIGN_UP_CLICK__");
      // 注册成功, 调用edm接口
      saveEdmUserInfo({ email, firstName, lastName });
      // localStorage.setItem(_CSR_ACCESS_TOKEN, JSON.stringify(customer?.customerAccessToken));
      useSetStorage(_CSR_ACCESS_TOKEN, res?.customerAccessToken);
      useSetStorage(_CSR_LOGIN_EMAIL, email);
      const buyerIdentity = {
        customerAccessToken: res?.customerAccessToken?.accessToken,
        email: email,
      };
      if (cartId.value) {
        await updateCustomerCartBuyerIdentity(buyerIdentity, cartId.value);
      }
      // 通知header更新用户名字
      await useCustomerName();
      const { myAccountPath } = useGetPath();
      console.log("password登录后", myAccountPath);
      setTimeout(() => (window.location.href = myAccountPath), 500);
      isLoading.value = false;
    }
  } catch (e) {
    console.error(e);
    isLoading.value = false;
  }
};

const saveEdmUserInfo = async ({ email, firstName, lastName }) => {
  const { originalSite, language } = document.body.dataset || {};
  await usePostUserinfo({
    email: email,
    firstName: firstName,
    lastName: lastName,
    country: originalSite?.toUpperCase(),
    language,
    entrance: "SU", // 表示是sign up的业务入口
    brand: useUcFirst(useBrand()),
    consentForEmailMarketing: acceptsMarketing.value ? "Y" : "N",
  });
};

// 点击注册按钮，切换到注册页面
const handleClickSign = () => {
  $emitEvents("onChange", "Login");
};

const handleChange = () => {
  acceptsMarketing.value = !acceptsMarketing.value;
  if (acceptsMarketing.value) {
    showError.value = false;
  }
};

defineExpose({
  registerCustomer,
});
</script>

<template>
  <div class="csr-login__wrap" v-Mdloading="isLoading">
    <div class="csr-login__top csr-login__top--sign">
      <div class="csr-login__title">
        {{ loginConfig.signUpTitle }}
      </div>
    </div>
    <div>
      <form id="csr-shopify-login" @submit.prevent="submitForm">
        <div class="csr-login__form">
          <div class="csr-login__form__flex">
            <FirstName
              ref="firstNameInput"
              :data-conf="{
                label: loginConfig.signUpFirstnameLabel,
                placeholder: loginConfig.signUpFirstnamePlaceholder,
                required: true,
                name: 'firstName',
                defErrorMsg: loginConfig.requiredFieldsPrompt,
              }"
            />
            <LastName
              ref="lastNameInput"
              :data-conf="{
                label: loginConfig.signUpLastnameLabel,
                placeholder: loginConfig.signUpLastnamePlaceholder,
                required: true,
                name: 'lastName',
                defErrorMsg: loginConfig.requiredFieldsPrompt,
              }"
            />
          </div>
          <Email
            ref="emailInput"
            :data-conf="{
              label: loginConfig.signUpEmailLabel,
              placeholder: loginConfig.signUpEmailPlaceholder,
              required: true,
              name: 'email',
              defErrorMsg: loginConfig.requiredFieldsPrompt,
              formatHint: loginConfig.emailFormatPrompt,
            }"
          />
          <Password
            ref="passwordInput"
            :data-conf="{
              label: loginConfig.signUpPasswordLabel,
              placeholder: loginConfig.signUpPasswordPlaceholder,
              required: true,
              minlength: '8',
              name: 'password',
              defErrorMsg: loginConfig.requiredFieldsPrompt,
              restrictionHint: loginConfig?.passwordSecurityPrompt,
            }"
            @change="passwordChange"
          ></Password>
          <div class="csr-login__form__last-item">
            <RetypePassword
              ref="retypePasswordInput"
              :data-conf="{
                label: loginConfig.signUpRePasswordLabel,
                placeholder: loginConfig.signUpRePasswordPlaceholder,
                required: true,
                minlength: '8',
                name: 'retypePassword',
                defErrorMsg: loginConfig.requiredFieldsPrompt,
                restrictionHint: loginConfig?.passwordSecurityPrompt,
                formatHint: loginConfig?.rePWSecurityPrompt,
              }"
              :password="passwordValue"
            ></RetypePassword>
          </div>
          <div></div>
        </div>
        <div
          v-if="loginConfig.signUpTermMandatory === 'true'"
          :class="[showError ? 'csr-login__policy error' : 'csr-login__policy']"
        >
          <MdCheckbox :value="acceptsMarketing" @change="handleChange">
            <span
              :class="[
                showError
                  ? 'csr-login__policy__content error'
                  : 'csr-login__policy__content',
              ]"
            >
              <span
                class="login__policy__txt"
                v-html="loginConfig.signUpTermAgreementLabel"
              ></span>

              <!-- {{ loginConfig.signUpTermAgreementLabel }}
            <a :href="loginConfig.signUpTermPrivacyPolicyLink" :target="loginConfig.target" class="csr-login__policy__txt" v-if="loginConfig.signUpTermPrivacyPolicyLink">{{ loginConfig.signUpTermPrivacyPolicyLinkText }}&nbsp;&nbsp;</a>
            <a :href="loginConfig.signUpTermLink" :target="loginConfig.target" class="csr-login__policy__txt" v-if="loginConfig.signUpTermLink">{{ loginConfig.signUpTermLinkText }}</a> -->
            </span>
          </MdCheckbox>
        </div>
        <div>
          <button class="csr-login__btn md-button-v2 md-button md-button--primary csr-login__btn_not_b2b" type="submit">{{ loginConfig.signUpButton }}</button>  
        </div>
      </form>
    </div>
    <div class="csr-login__sub-title csr-sub-title-small-margin">
      {{ loginConfig.signUpSubtitle }}
      <a class="csr-login__sub-title__btn" href="#" @click="handleClickSign">{{
        loginConfig.signInQuickLinkName
      }}</a>
    </div>
  </div>
</template>